import { useEffect, useMemo, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded'
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded'
import { CURATED_CURRENCY_CODES, ISO_4217_CURRENCIES, getIsoCurrency, type IsoCurrency } from '../../utils/iso4217'
import { detectLocaleCurrency } from '../../utils/localeCurrency'
import { WorkspaceCurrencyPickerSheet } from './WorkspaceCurrencyPickerSheet'
import { SetupStepFooter } from './SetupStepFooter'
import { useNls } from '../../nls/nlsProvider'
import { Button } from '../Common/Button'
import { IconButton } from '../Common/IconButton'
import { Pressable } from '../Common/Pressable'

export interface WorkspaceCurrencyPickerStepProps {
  value?: string
  onChange?: (currency: string) => void
  onContinue: (currency: string) => void
  onBack?: () => void
  className?: string
}

export const WorkspaceCurrencyPickerStep = ({
  value,
  onChange,
  onContinue,
  onBack,
  className = '',
}: WorkspaceCurrencyPickerStepProps) => {
  const { t } = useNls()
  const detectedCurrency = useMemo(() => detectLocaleCurrency(), [])
  const [selectedCurrency, setSelectedCurrency] = useState<string>(
    () => (value || detectedCurrency || 'USD').toUpperCase(),
  )
  const [isSheetOpen, setIsSheetOpen] = useState(false)
  const [wasShownFullList, setWasShownFullList] = useState(false)
  const [showHelpPopover, setShowHelpPopover] = useState(false)

  // Sync with incoming value if changed externally
  useEffect(() => {
    if (value && value.toUpperCase() !== selectedCurrency) {
      setSelectedCurrency(value.toUpperCase())
    }
  }, [value, selectedCurrency])

  const handleSelect = (code: string) => {
    const next = code.toUpperCase()
    setSelectedCurrency(next)
    onChange?.(next)
  }

  const handleContinue = () => {
    // Analytics event per §2.7
    console.debug('[analytics:stub] workspace.currency.picker.selected', {
      detectedCurrency,
      chosenCurrency: selectedCurrency,
      wasDetected: detectedCurrency.toUpperCase() === selectedCurrency,
      wasShownFullList,
    }) // TODO(workspace-currency-analytics)

    onContinue(selectedCurrency)
  }

  // Build the chip list: curated 20 + (optional 21st detected/custom chip)
  const displayCurrencies = useMemo<IsoCurrency[]>(() => {
    const curated = CURATED_CURRENCY_CODES.map((code) => getIsoCurrency(code)!).filter(Boolean)

    const extras: IsoCurrency[] = []
    // If detected is not in curated, prepend as 21st chip
    if (detectedCurrency && !CURATED_CURRENCY_CODES.includes(detectedCurrency as any)) {
      const det = getIsoCurrency(detectedCurrency)
      if (det) extras.push(det)
    }

    // If currently selected is outside both curated and detected, add it
    if (
      selectedCurrency &&
      !CURATED_CURRENCY_CODES.includes(selectedCurrency as any) &&
      selectedCurrency !== detectedCurrency.toUpperCase()
    ) {
      const sel = getIsoCurrency(selectedCurrency)
      if (sel) extras.push(sel)
    }

    return [...extras, ...curated]
  }, [detectedCurrency, selectedCurrency])

  // Keyboard '?' support for lock explainer
  const handleKeyDown = (e: ReactKeyboardEvent) => {
    if (e.key === '?') {
      e.preventDefault()
      setShowHelpPopover((prev) => !prev)
    }
  }

  return (
    <div
      className={`workspaceCurrencyPickerStep ${className}`}
      onKeyDown={handleKeyDown}
      style={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        boxSizing: 'border-box',
        height: '100%',
        minHeight: 0,
      }}
    >
      <div className="workspaceCurrencyPickerStep__body">
      {/* Hero Header */}
      <h1
        style={{
          fontSize: 'clamp(26px, 4vw, 36px)',
          fontWeight: 700,
          letterSpacing: '-0.025em',
          margin: '0 0 10px',
          color: 'var(--text-strong)',
        }}
      >
        {t('NLSWorkspaceCurrencyHeroTitle')}
      </h1>

      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          margin: '0 0 28px',
        }}
      >
        <p
          style={{
            fontSize: 15,
            color: 'var(--muted)',
            margin: 0,
          }}
        >
          {t('NLSWorkspaceCurrencyHeroSubtitle')}
        </p>
        <IconButton
          variant="tertiary"
          size="sm"
          label="Why can't I change currency later?"
          icon={<HelpOutlineRoundedIcon style={{ fontSize: 16 }} />}
          onClick={() => setShowHelpPopover((prev) => !prev)}
        />
      </div>

      {showHelpPopover ? (
        <div
          role="note"
          style={{
            background: 'var(--surface-raised)',
            border: '1px solid var(--border)',
            borderRadius: 10,
            padding: '10px 16px',
            marginBottom: 20,
            maxWidth: 520,
            fontSize: 13,
            color: 'var(--text)',
            textAlign: 'left',
            boxShadow: 'var(--shadow-pop)',
          }}
        >
          {t('NLSWorkspaceCurrencyLockedTooltip', { code: selectedCurrency })} Multi-currency support across historical audit entries, budget ledger summaries, and stock valuations requires workspace-level currency immutability.
        </div>
      ) : null}

      {/* Currency Chips Grid (5 columns on desktop, responsive) */}
      <div
        role="radiogroup"
        aria-label={t('NLSWorkspaceCurrencyHeroTitle')}
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))',
          gap: 12,
          width: '100%',
          marginBottom: 20,
        }}
      >
        {displayCurrencies.map((c) => {
          const isSelected = selectedCurrency === c.code
          const isDetected = detectedCurrency.toUpperCase() === c.code

          return (
            <Pressable
              key={c.code}
              role="radio"
              aria-checked={isSelected}
              onClick={() => handleSelect(c.code)}
              style={{
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '16px 8px 12px',
                minHeight: 110,
                borderRadius: 12,
                border: isSelected
                  ? '2px solid var(--accent)'
                  : isDetected
                    ? '2px dashed var(--accent)'
                    : '1px solid var(--border)',
                background: isSelected
                  ? 'color-mix(in srgb, var(--accent) 18%, var(--surface-card))'
                  : 'var(--control)',
                color: isSelected ? 'var(--text-strong)' : 'inherit',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
                boxShadow: isSelected
                  ? '0 0 0 1px var(--accent), var(--shadow-pop)'
                  : 'none',
              }}
            >
              {/* Detected micro-badge */}
              {isDetected ? (
                <span
                  style={{
                    position: 'absolute',
                    top: 6,
                    left: 6,
                    fontSize: 9,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    padding: '2px 5px',
                    borderRadius: 4,
                    background: 'color-mix(in srgb, var(--accent) 20%, transparent)',
                    color: 'var(--accent)',
                  }}
                >
                  {t('NLSWorkspaceCurrencyDetectedBadge')}
                </span>
              ) : null}

              {/* Selected Checkmark Badge */}
              {isSelected ? (
                <span
                  style={{
                    position: 'absolute',
                    top: 6,
                    right: 6,
                    width: 18,
                    height: 18,
                    borderRadius: '50%',
                    background: 'var(--accent)',
                    color: 'var(--on-accent)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CheckRoundedIcon style={{ fontSize: 13 }} />
                </span>
              ) : null}

              {/* Row 1: Flag + ISO Code */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  fontSize: 16,
                  fontWeight: 600,
                  color: 'var(--text-strong)',
                  marginBottom: 4,
                }}
              >
                <span>{c.flag}</span>
                <span>{c.code}</span>
              </div>

              {/* Row 2: Currency Symbol (visual anchor) */}
              <div
                style={{
                  fontSize: 26,
                  fontWeight: 300,
                  lineHeight: 1.1,
                  color: isSelected ? 'var(--accent)' : 'var(--text-strong)',
                  margin: '2px 0 4px',
                }}
              >
                {c.symbol}
              </div>

              {/* Row 3: Short Name */}
              <div
                style={{
                  fontSize: 11,
                  color: 'var(--muted)',
                  maxWidth: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {c.name}
              </div>
            </Pressable>
          )
        })}
      </div>

      {/* Ghost link to show all currencies sheet */}
      <Button
        variant="secondary"
        size="md"
        style={{ marginBottom: 28 }}
        onClick={() => {
          setIsSheetOpen(true)
          setWasShownFullList(true)
        }}
      >
        {t('NLSWorkspaceCurrencyShowAll', { count: ISO_4217_CURRENCIES.length })}
      </Button>

      </div>{/* /__body */}

      {/* Navigation / Action Row — pinned foot */}
      <SetupStepFooter className="workspaceCurrencyPickerStep__foot" onBack={onBack}>
        <Button variant="primary" trailingIcon={<ArrowForwardRoundedIcon />} onClick={handleContinue}>
          {t('NLSWorkspaceCurrencyContinueCTA', { code: selectedCurrency })}
        </Button>
      </SetupStepFooter>

      {/* Bottom Sheet */}
      <WorkspaceCurrencyPickerSheet
        isOpen={isSheetOpen}
        onClose={() => setIsSheetOpen(false)}
        onSelect={handleSelect}
        selectedCode={selectedCurrency}
      />
    </div>
  )
}
